import { generateClient } from "aws-amplify/data";
import { fetchUserAttributes } from "aws-amplify/auth";
import type { Schema } from "amplify/data/resource";
import { DEV_MERCHANT_JOB_TYPE } from "@cpos/shared/src/dev-merchant/spec";
import type {
  CreateMerchantRepository,
  SavedPreset,
} from "./create_merchant_bloc";
import type { JobRecord, JobStatus } from "./types";

const client = generateClient<Schema>();

const JOB_TYPE = DEV_MERCHANT_JOB_TYPE;

function toJobRecord(raw: Schema["Job"]["type"]): JobRecord {
  return {
    id: raw.id,
    type: raw.type,
    status: (raw.status ?? "pending") as JobStatus,
    triggeredBy: raw.triggeredBy,
    total: raw.total ?? null,
    processed: raw.processed ?? 0,
    counters:
      typeof raw.counters === "string"
        ? raw.counters
        : raw.counters != null
          ? JSON.stringify(raw.counters)
          : null,
    result:
      typeof raw.result === "string"
        ? raw.result
        : raw.result != null
          ? JSON.stringify(raw.result)
          : null,
    error: raw.error ?? null,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
  };
}

export const createMerchantRepository: CreateMerchantRepository = {
  async kickoff(spec, count) {
    const result = await client.mutations.createDevMerchant({
      spec: JSON.stringify(spec),
      count,
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
    if (!result.data?.success || !result.data.jobId) {
      throw new Error(result.data?.error ?? "createDevMerchant failed");
    }
    return result.data.jobId;
  },

  async findRunningJob() {
    const result = await client.models.Job.list({
      filter: {
        type: { eq: JOB_TYPE },
        or: [{ status: { eq: "running" } }, { status: { eq: "pending" } }],
      },
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
    const first = result.data[0];
    return first ? toJobRecord(first) : null;
  },

  subscribeToJob(jobId, onUpdate, onError) {
    const sub = client.models.Job.onUpdate({
      filter: { id: { eq: jobId } },
    }).subscribe({
      next: (data) => {
        onUpdate(toJobRecord(data));
      },
      error: (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        onError(error);
      },
    });

    return () => sub.unsubscribe();
  },

  async listPresets() {
    const result = await client.models.DevMerchantPreset.list();
    if (result.errors?.length) throw new Error(result.errors[0].message);
    const presets: SavedPreset[] = [];
    for (const raw of result.data) {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(raw.spec as string) as Record<string, unknown>;
      } catch {
        console.warn(
          `Skipping DevMerchantPreset ${raw.id}: spec is not valid JSON`,
        );
        continue;
      }
      const { groups, ...spec } = parsed;
      presets.push({
        id: raw.id,
        name: raw.name,
        description: raw.description ?? null,
        groups: Array.isArray(groups) ? (groups as string[]) : [],
        spec: spec as SavedPreset["spec"],
      });
    }
    return presets;
  },

  async savePreset(name, description, spec, groups) {
    const attributes = await fetchUserAttributes();
    const result = await client.models.DevMerchantPreset.create({
      name,
      description: description ?? null,
      spec: JSON.stringify({ ...spec, groups }),
      createdBy: attributes.email ?? "unknown",
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
  },

  async deletePreset(id) {
    const result = await client.models.DevMerchantPreset.delete({ id });
    if (result.errors?.length) throw new Error(result.errors[0].message);
  },
};
